import { confirmDialog as confirm } from "@/components/ConfirmProvider";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { techRoadmap, toggleRoadmapTopic, regenerateRoadmap } from "@/lib/api";
import { Map, Check, Loader2, RefreshCw, Youtube, Clock, ArrowRight, Building2, BookOpen, Star } from "lucide-react";
import { CourseCard } from "@/components/wishire/CourseCards";

const KIND_STYLE = {
  concept:       "bg-blue-50 text-blue-700 border-blue-200",
  coding:        "bg-emerald-50 text-emerald-700 border-emerald-200",
  tool:          "bg-teal-50 text-teal-700 border-teal-200",
  project:       "bg-purple-50 text-purple-700 border-purple-200",
  aptitude:      "bg-amber-50 text-amber-700 border-amber-200",
  communication: "bg-pink-50 text-pink-700 border-pink-200",
  interview:     "bg-rose-50 text-rose-700 border-rose-200",
};

export default function RoadmapPage({ embedded = false, reloadKey = 0, onData }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [regenBusy, setRegenBusy] = useState(false);

  useEffect(() => {
    techRoadmap().then(({ data }) => { setData(data); onData?.(data); }).catch(() => setData(null)).finally(() => setLoading(false));
  }, [reloadKey]); // eslint-disable-line

  const toggle = async (topicId) => {
    setBusy(topicId);
    try { const { data: next } = await toggleRoadmapTopic(topicId); setData(next); onData?.(next); }
    catch (_) {} finally { setBusy(null); }
  };

  const regenerate = async () => {
    if (!(await confirm({ title: "Start over?", description: "Generate a fresh plan? Your ticked steps will reset.", danger: true }))) return;
    setRegenBusy(true);
    try { const { data: next } = await regenerateRoadmap(); setData(next); }
    catch (_) {} finally { setRegenBusy(false); }
  };

  if (loading) return (
    <div className={`${embedded ? "py-8" : "container-x py-16"} text-stone-500 flex items-center gap-2`} data-testid="roadmap-loading">
      <Loader2 size={18} className="animate-spin" /> Building your plan… (first visit takes ~20s)
    </div>
  );

  if (!data) return <div className={`${embedded ? "py-8" : "container-x py-16"} text-stone-500`}>Could not load your plan — refresh to retry.</div>;

  const { progress } = data;

  return (
    <div className={embedded ? "" : "container-x py-10 md:py-14"} data-testid="roadmap-page">
      <div className={`flex items-start justify-between gap-4 flex-wrap ${embedded ? "mt-3" : ""}`}>
        {embedded ? (
          <div className="max-w-2xl">
            {data.summary && <p className="text-sm text-stone-700 leading-relaxed bg-[#F5F3ED] rounded-2xl px-4 py-3">{data.summary}</p>}
            {data.for_companies?.length > 0 && (
              <p className="mt-3 text-xs text-stone-500 flex flex-wrap items-center gap-1.5" data-testid="plan-companies"><Building2 size={13} className="text-[#C2410C]" /> Built for your campus drives:
                {data.for_companies.slice(0, 8).map((c) => <span key={c} className="px-2 py-0.5 rounded-full bg-white border border-stone-200 text-stone-700">{c}</span>)}{data.for_companies.length > 8 && <span>+{data.for_companies.length - 8} more</span>}</p>
            )}
          </div>
        ) : (
        <div className="max-w-2xl">
          <span className="overline">My Plan</span>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Your 12-week plan to <span className="italic text-stone-500">get placed.</span></h1>
          <p className="text-sm text-stone-600 mt-3 leading-relaxed">
            You don't need to pick a role yet. This plan gets you ready for the companies actually coming to your campus, in the order they screen freshers — aptitude first, then your course's fundamentals, problem solving, your resume, and interviews. Works for every course: B.Tech, BCA, BBA, B.Com, MBA, BA, B.Sc. Tick steps off; your coach nudges you on the rest.
          </p>
          {data.summary && <p className="text-sm text-stone-700 mt-3 leading-relaxed bg-[#F5F3ED] rounded-2xl px-4 py-3">{data.summary}</p>}
          {data.for_companies?.length > 0 ? (
            <p className="mt-3 text-xs text-stone-500 flex flex-wrap items-center gap-1.5" data-testid="plan-companies">
              <Building2 size={13} className="text-[#C2410C]" /> Built for your campus drives:
              {data.for_companies.slice(0, 8).map((c) => <span key={c} className="px-2 py-0.5 rounded-full bg-white border border-stone-200 text-stone-700">{c}</span>)}
              {data.for_companies.length > 8 && <span>+{data.for_companies.length - 8} more</span>}
            </p>
          ) : (
            <p className="mt-3 text-xs text-stone-500">No drives announced at your campus yet — this plan covers what most campus recruiters test. It refreshes automatically when companies are added.</p>
          )}
        </div>
        )}
        <button onClick={regenerate} disabled={regenBusy} data-testid="roadmap-regenerate"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-stone-300 text-sm text-stone-600 hover:border-stone-900 transition-colors disabled:opacity-60">
          {regenBusy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Start over
        </button>
      </div>

      <div className={`rounded-3xl border border-stone-200 bg-white p-6 ${embedded ? "mt-5" : "mt-8"}`} data-testid="roadmap-progress">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <p className="text-sm text-stone-600"><span className="font-semibold text-stone-900">{progress.completed}</span> of {progress.total} topics done</p>
          <p className="text-xs text-stone-500 inline-flex items-center gap-1.5"><Clock size={13} /> ~{progress.hours_left}h of work left</p>
        </div>
        <div className="mt-3 h-2.5 rounded-full bg-stone-100 overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-[#C2410C] to-amber-500 transition-all" style={{ width: `${progress.percent}%` }} />
        </div>
        <p className="text-right text-xs font-mono text-stone-400 mt-1.5">{progress.percent}%</p>
      </div>

      {data.recommended_courses?.length > 0 && (
        <div className="mt-8" data-testid="plan-courses">
          <p className="text-xs font-mono uppercase tracking-widest text-[#C2410C] inline-flex items-center gap-2"><BookOpen size={13} /> Courses to do alongside this plan</p>
          <div className="mt-3 grid md:grid-cols-3 gap-3">{data.recommended_courses.map((c) => <CourseCard key={c.course_id} c={c} compact onChange={() => techRoadmap().then(({ data }) => setData(data)).catch(() => {})} />)}</div>
        </div>
      )}

      <div className="mt-10 space-y-8">
        {data.phases.map((p, pi) => (
          <div key={pi} className="relative rounded-3xl border border-stone-200 bg-white p-6 md:p-8" data-testid={`roadmap-phase-${pi}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-4">
                <span className={`shrink-0 w-10 h-10 rounded-2xl flex items-center justify-center font-display text-lg ${p.done_count === p.topic_count ? "bg-emerald-100 text-emerald-700" : "bg-stone-900 text-amber-300"}`}>
                  {p.done_count === p.topic_count ? <Check size={18} /> : pi + 1}
                </span>
                <div>
                  <h2 className="font-display text-2xl">{p.title}</h2>
                  <p className="text-sm text-stone-600 mt-1">{p.goal}</p>
                </div>
              </div>
              <div className="text-right shrink-0">
                {p.weeks && <p className="text-xs font-mono uppercase tracking-widest text-stone-400">{p.weeks}</p>}
                <p className="text-xs text-stone-500 mt-1">{p.done_count}/{p.topic_count} done</p>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              {p.topics.map((t) => (
                <div key={t.topic_id} className={`flex items-start gap-3.5 rounded-2xl border px-4 py-3.5 transition-colors ${t.done ? "border-emerald-200 bg-emerald-50/50" : "border-stone-200 hover:border-stone-300"}`}>
                  <button onClick={() => toggle(t.topic_id)} disabled={busy === t.topic_id} data-testid={`topic-check-${t.topic_id}`}
                    className={`shrink-0 mt-0.5 w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-colors ${t.done ? "bg-emerald-500 border-emerald-500 text-white" : "border-stone-300 hover:border-stone-900"}`}>
                    {busy === t.topic_id ? <Loader2 size={12} className="animate-spin text-stone-400" /> : t.done ? <Check size={14} /> : null}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`text-sm font-medium ${t.done ? "text-stone-400 line-through" : "text-stone-900"}`}>{t.title}</p>
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${KIND_STYLE[t.kind] || KIND_STYLE.concept}`}>{t.kind}</span>
                      <span className="text-[10px] font-mono text-stone-400">~{t.est_hours}h</span>
                    </div>
                    {t.detail && <p className="text-xs text-stone-500 mt-1 leading-relaxed">{t.detail}</p>}
                    <div className="flex items-center gap-3 mt-2">
                      <a href={t.resource_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600 hover:text-red-700">
                        <Youtube size={13} /> Find tutorial
                      </a>
                      {t.cta && (
                        <Link to={t.cta.path} className="inline-flex items-center gap-1 text-xs font-medium text-[#C2410C] hover:underline">
                          {t.cta.label} <ArrowRight size={11} />
                        </Link>
                      )}
                    </div>
                    {t.courses?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {t.courses.map((c) => (
                          <a key={c.course_id} href={c.url || "#courses"} target={c.url ? "_blank" : undefined} rel="noreferrer"
                            className={`inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border ${c.required ? "border-[#C2410C]/40 bg-amber-50 text-[#C2410C]" : "border-stone-200 bg-white text-stone-700"} hover:border-stone-900`}>
                            {c.required ? <Star size={10} className="fill-[#C2410C]" /> : <BookOpen size={10} />} {c.title}<span className="text-stone-400">· {c.source === "college" ? "college" : "Wishire"}</span>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <p className="mt-8 text-xs text-stone-400 flex items-center gap-1.5"><Map size={13} /> Your plan updates automatically when your college adds companies or you change your branch or target role — ticked steps carry over. Checking off steps feeds your prep streak.</p>
    </div>
  );
}
